import React, { useState, useEffect } from 'react';
import { X, Check, Loader2, Link2 } from 'lucide-react';
import baseapi from '../../services/baseapi';
import { showError, showSuccess } from '../../utils/toast';
import { useClientLevel } from '../../hooks/useClientLevel';
import MultiSelectDropdown from '../../components/MultiSelectDropdown';

interface AssignSwitchModalProps {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
    groupId: number;
    groupName: string;
}

interface SwitchAsset {
    asset_id: number;
    name: string;
    hostname: string;
    asset_type: string;
    switch_group_id: number | null;
    group_name: string | null;
}

export default function AssignSwitchModal({
    open,
    onClose,
    onSuccess,
    groupId,
    groupName
}: AssignSwitchModalProps) {
    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(false);
    const [switches, setSwitches] = useState<SwitchAsset[]>([]);
    const [selectedValues, setSelectedValues] = useState<string[]>([]);
    const level = useClientLevel();

    useEffect(() => {
        if (open && level?.id) {
            fetchSwitches();
        }
    }, [open, level?.id, groupId]);

    const fetchSwitches = async () => {
        setFetching(true);
        try {
            const response = await baseapi.get(`/switch_groups/switches?client_id=${level.id}`);
            if (response.data.status) {
                const data: SwitchAsset[] = response.data.data || [];
                setSwitches(data);

                // Pre-select switches that are already assigned to this group
                const initiallySelected = data
                    .filter(sw => sw.switch_group_id === groupId)
                    .map(sw => sw.asset_id.toString());
                setSelectedValues(initiallySelected);
            } else {
                showError('Failed to fetch switches');
            }
        } catch (error) {
            console.error('Error fetching switches:', error);
            showError('Error fetching switches');
        } finally {
            setFetching(false);
        }
    };

    if (!open) return null;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        try {
            const assetIds = selectedValues.map(v => Number(v));
            const payload = {
                client_id: Number(level?.id),
                asset_ids: assetIds,
                group_id: groupId
            };

            const response = await baseapi.post('/switch_groups/assign', payload);
            if (response.data.status) {
                showSuccess('Switches assigned successfully');
                onSuccess();
                onClose();
            } else {
                showError(response.data.message || 'Failed to assign switches');
            }
        } catch (error: any) {
            console.error('Error assigning switches:', error);
            showError(error.response?.data?.message || error.message || 'An error occurred');
        } finally {
            setLoading(false);
        }
    };

    // Transform switches to options for MultiSelectDropdown
    const dropdownOptions = switches.map(sw => {
        let label = sw.name || sw.hostname;
        if (sw.switch_group_id !== null) {
            if (sw.switch_group_id === groupId) {
                // Already assigned to this group
                label = `${label} (Assigned to this group)`;
            } else {
                // Assigned to another group
                label = `${label} (Assigned to ${sw.group_name || 'another group'})`;
            }
        }
        return {
            label,
            value: sw.asset_id.toString()
        };
    });

    return (
        <>
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9999]" onClick={onClose} />
            <div className="fixed inset-0 flex items-center justify-center z-[10000] p-4">
                <div className="w-full max-w-lg bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 shadow-2xl rounded-xl text-gray-900 dark:text-white relative overflow-hidden">
                    <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-orange-400/40 to-transparent" />
                    
                    {/* Header */}
                    <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-800 bg-gray-50/50 dark:bg-transparent">
                        <div className="flex items-center gap-2">
                            <Link2 className="w-4 h-4 text-orange-500 dark:text-orange-400" />
                            <h2 className="text-lg font-bold text-gray-900 dark:text-white">
                                Assign Switches
                            </h2>
                        </div>
                        <button onClick={onClose} className="text-gray-400 hover:text-gray-700 dark:hover:text-white transition-colors p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800">
                            <X className="w-4 h-4" />
                        </button>
                    </div>

                    {/* Content */}
                    <form onSubmit={handleSubmit} className="p-6 space-y-4">
                        <div className="bg-gray-50 dark:bg-gray-800 p-4 border border-gray-200 dark:border-gray-800 rounded-lg">
                            <span className="text-xs text-gray-500 dark:text-gray-400 uppercase font-semibold block tracking-wider mb-1">Target Group</span>
                            <span className="text-base font-bold text-orange-600 dark:text-orange-400">{groupName}</span>
                        </div>

                        {fetching ? (
                            <div className="flex flex-col items-center justify-center py-12 gap-2">
                                <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
                                <span className="text-xs text-gray-500 font-medium">Fetching switches...</span>
                            </div>
                        ) : (
                            <div>
                                <MultiSelectDropdown
                                    label="Select Switches"
                                    options={dropdownOptions}
                                    value={selectedValues}
                                    onChange={(values) => setSelectedValues(values)}
                                    placeholder="Select switches to assign..."
                                    variant="config"
                                    maxVisibleTags={9999}
                                />
                                <p className="text-[10px] text-gray-500 mt-2 leading-relaxed">
                                    Switches already assigned to other groups will be reassigned to this group upon selection and save.
                                </p>
                            </div>
                        )}

                        {/* Footer */}
                        <div className="flex justify-end gap-2 pt-4 border-t border-gray-200 dark:border-gray-800">
                            <button
                                type="button"
                                onClick={onClose}
                                disabled={loading}
                                className="flex items-center gap-1 px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 hover:border-gray-400 dark:hover:border-gray-600 disabled:opacity-50"
                            >
                                Cancel
                            </button>

                            <button
                                type="submit"
                                disabled={loading || fetching}
                                className="flex items-center gap-1.5 px-4 py-1.5 text-sm bg-gradient-to-r from-orange-600 to-orange-700 text-white rounded-lg font-semibold hover:from-orange-700 hover:to-orange-800 disabled:opacity-50 min-w-[100px] justify-center"
                            >
                                {loading ? (
                                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                ) : (
                                    <Check className="w-3.5 h-3.5" />
                                )}
                                Save
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </>
    );
}
